/**
 * Notification Bell Component
 * Displays notification bell with unread count badge
 */

import React, { useEffect, useRef, useState } from "react";
import {
	useListNotificationsQuery,
	useMarkAllNotificationsReadMutation,
} from "../../store/api/notificationsApi";
import Icon from "../AppIcon";
import NotificationDropdown from "./NotificationDropdown";

const NotificationBell: React.FC = () => {
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);

	// Fetch notifications
	const {
		data: notifications = [],
		refetch,
		isLoading,
		error,
	} = useListNotificationsQuery({});
	const [markAllRead] = useMarkAllNotificationsReadMutation();

	// Count unread notifications
	const unreadCount = notifications.filter((n) => !n.is_read).length;

	// Log errors and data for debugging
	useEffect(() => {
		if (error) {
			console.error("Failed to fetch notifications:", error);
		}
	}, [error, notifications]);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isOpen]);

	const handleToggle = () => {
		if (!isOpen) {
			// Refetch notifications when opening dropdown
			refetch();
		}
		setIsOpen(!isOpen);
	};

	const handleMarkAllRead = async () => {
		try {
			await markAllRead().unwrap();
			refetch();
		} catch (error) {
			console.error("Failed to mark all as read:", error);
		}
	};

	return (
		<div className="relative" ref={dropdownRef}>
			{/* Bell Button */}
			<button
				onClick={handleToggle}
				className="relative p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
				aria-label="Notifications"
			>
				<Icon name="Bell" size={20} />
				{unreadCount > 0 && (
					<span className="absolute top-0 right-0 flex items-center justify-center w-5 h-5 text-xs font-medium text-white bg-destructive rounded-full">
						{unreadCount > 9 ? "9+" : unreadCount}
					</span>
				)}
			</button>

			{/* Dropdown */}
			{isOpen && (
				<NotificationDropdown
					notifications={notifications}
					onMarkAllRead={handleMarkAllRead}
					onClose={() => setIsOpen(false)}
				/>
			)}
		</div>
	);
};

export default NotificationBell;
